/* FASE 13 (WS4) — Página corporativa "For buyers": el proceso real del
   comprador, del registro a la factura. */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { H_SECTION, PrimaryCta, SecondaryCta, SUBLINE } from "@/components/marketing/Sections";
import { BRAND } from "@/config/brand";
import { useLocalePath } from "@/lib/locale";
import { PUBLIC_BASE, useJsonLd, usePageMeta } from "@/lib/seo";

export default function ForBuyers() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  usePageMeta({
    title: `${t("fb.eyebrow")} — ${BRAND.name}`,
    description: t("fb.sub"),
    localized: true,
  });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `${t("fb.eyebrow")} — ${BRAND.name}`,
    description: t("fb.sub"),
    publisher: { "@type": "Organization", name: BRAND.name, url: PUBLIC_BASE },
  });

  return (
    <div data-testid="for-buyers-page">
      {/* Hero */}
      <section className="max-w-[980px] mx-auto px-6 pt-20 pb-16 text-center flex flex-col items-center gap-5">
        <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#6e6e73]">{t("fb.eyebrow")}</p>
        <h1 className={`${H_SECTION} text-[#1d1d1f]`} data-testid="fb-title">{t("fb.title")}</h1>
        <p className={`${SUBLINE} text-[#4b4b50] max-w-2xl`}>{t("fb.sub")}</p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
          <PrimaryCta small to="/auctions" testid="fb-cta-browse">{t("nav.browseAuctions")}</PrimaryCta>
          <SecondaryCta small to="/resources" testid="fb-cta-guide">{t("fb.ctaGuide")}</SecondaryCta>
        </div>
      </section>

      {/* Proceso del comprador — 5 pasos numerados */}
      <section className="bg-[#f5f5f7]">
        <div className="max-w-[780px] mx-auto px-6 py-20 flex flex-col gap-10">
          {[1, 2, 3, 4, 5].map((n) => (
            <div key={n} className="flex items-start gap-6" data-testid={`fb-step-${n}`}>
              <span
                aria-hidden="true"
                className="shrink-0 w-9 h-9 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold flex items-center justify-center"
              >
                {n}
              </span>
              <div className="flex flex-col gap-1.5">
                <h2 className="text-[17px] font-semibold text-[#1d1d1f]">{t(`fb.b${n}t`)}</h2>
                <p className="text-sm text-[#3f3f44] leading-relaxed">{t(`fb.b${n}d`)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-[780px] mx-auto px-6 py-20 text-center flex flex-col items-center gap-6">
        <h2 className="text-2xl lg:text-3xl font-semibold text-[#1d1d1f]">{t("fb.ctaTitle")}</h2>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <PrimaryCta small to="/auctions" testid="fb-final-browse">{t("nav.browseAuctions")}</PrimaryCta>
          <Link to={lp("/resources")} data-testid="fb-final-guide" className="text-sm font-semibold text-[#1d4d3b] hover:underline">
            {t("fb.ctaGuide")} →
          </Link>
        </div>
      </section>
    </div>
  );
}
